import { useEffect, useState } from "react";

import type { BlsChannel } from "./useBlsSession";
import type { BlsSettings } from "../domain/blsChannel";
import { DEFAULT_CYCLES_PER_MINUTE } from "../domain/blsMotion";

export const DEFAULT_BLS_SETTINGS: BlsSettings = {
    mode: "both",
    path: "horizontal",
    cyclesPerMinute: DEFAULT_CYCLES_PER_MINUTE,
    dotSize: "medium",
    dotColor: "white",
    tactile: false,
};

/** Often enough that the counter keeps up with a fast set, rarely enough to be free. */
const COUNTER_REFRESH_MS = 250;

/**
 * The settings the next set starts with, and while one runs, its own.
 *
 * Changed before a set, they wait for Start; changed during one, they reach
 * the patient at once without moving the dot.
 */
export function useBlsSettings(channel: BlsChannel) {
    const { state } = channel;
    const [draft, setDraft] = useState<BlsSettings>(state?.settings ?? DEFAULT_BLS_SETTINGS);
    const settings = state?.settings ?? draft;

    const change = (patch: Partial<BlsSettings>) => {
        const next = { ...settings, ...patch };
        setDraft(next);
        if (state) channel.updateSettings(next);
    };

    return { settings, change };
}

/**
 * Whole passes of the current set, refreshed while it runs.
 *
 * A pass is one full left→right→left cycle. Re-renders only when the whole
 * number changes, so the counter costs nothing between passes.
 */
export function usePassCount(channel: BlsChannel): number {
    const { state, cyclesNow } = channel;
    const [passes, setPasses] = useState(() => Math.floor(cyclesNow()));

    useEffect(() => {
        const update = () => setPasses(Math.floor(cyclesNow()));
        update();
        if (!state?.running) return;
        const handle = window.setInterval(update, COUNTER_REFRESH_MS);
        return () => window.clearInterval(handle);
    }, [cyclesNow, state]);

    return passes;
}
